import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AbstractDialogResultData, AbstractFormDialog, AbstractFormDialogData, DIALOG_RETURN_TYPES, FormDialogComponent } from '@next/core-lib/dialog';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { map, Observable, startWith } from 'rxjs';

import { CropVariety, VarietyPropertiesTGAResponse, VarietyPropertiesTGAUpsertRequest } from '../../models/tga-varieties.model';

export interface VarietyPropertyDialogData extends AbstractFormDialogData {
  varietyProperty?: VarietyPropertiesTGAResponse;
  cropVarieties: CropVariety[];
}

@Component({
  selector: 'app-variety-property-dialog',
  templateUrl: './variety-property-dialog.component.html',
  styleUrls: ['./variety-property-dialog.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatSlideToggle,
    FormDialogComponent,
    AgrI18nSharedModule,
  ],
})
export class VarietyPropertyDialogComponent extends AbstractFormDialog<VarietyPropertiesTGAUpsertRequest> implements OnInit {
  public readonly data = inject<VarietyPropertyDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
    inject<MatDialogRef<VarietyPropertyDialogComponent, AbstractDialogResultData<VarietyPropertiesTGAUpsertRequest>>>(MatDialogRef);
  filteredCropVarieties$!: Observable<CropVariety[]>;
  isEditMode = !!this.data.varietyProperty;

  formGroup: FormGroup = this.formBuilder.group({
    id: [null as number | null],
    cropVarietySelection: [null],
    ediCode: [null as number | null, [Validators.required, Validators.min(1), Validators.max(2147483647)]],
    knolZetting: [null as number | null, [Validators.min(0), Validators.max(10)]],
    vroegheidRas: [null as number | null, [Validators.min(0), Validators.max(10)]],
    rasScorePhytophthora: [null as number | null, [Validators.min(0), Validators.max(10)]],
    rasScoreSchurft: [null as number | null, [Validators.min(0), Validators.max(10)]],
    rasScoreZwarteSpikkel: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingCompany: [null as string | null],
    nvwaGloboderaRos14Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingGloboderaRos14Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaGloboderaRos14Rv: [null as number | null, [Validators.min(0), Validators.max(100)]],
    nvwaGloboderaRos23Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingGloboderaRos23Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaGloboderaRos23Rv: [null as number | null, [Validators.min(0), Validators.max(100)]],
    nvwaGloboderaPal2Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingGloboderaPal2Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaGloboderaPal2Rv: [null as number | null, [Validators.min(0), Validators.max(100)]],
    nvwaGloboderaPal3Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingGloboderaPal3Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaGloboderaPal3Rv: [null as number | null, [Validators.min(0), Validators.max(100)]],
    nvwaGloboderaRos5Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    tradingGloboderaRos5Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaGloboderaRos5Rv: [null as number | null, [Validators.min(0), Validators.max(100)]],
    scoreAlternaria: [null as number | null, [Validators.min(0), Validators.max(10)]],
    scoreFusarium: [null as number | null, [Validators.min(0), Validators.max(10)]],
    scorePhytophthoraTuber: [null as number | null, [Validators.min(0), Validators.max(10)]],
    scoreLeafrollVirus: [null as number | null, [Validators.min(0), Validators.max(10)]],
    scoreVirusYntnTuber: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaWartDiseaseF1Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaWartDiseaseF26Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    nvwaWartDiseaseF18Score: [null as number | null, [Validators.min(0), Validators.max(10)]],
    wortellesieaaltjeTolerant: [false],
  });

  displayCropVariety() {
    return (ediCode?: number) => (ediCode ? this.data.cropVarieties.find((cv: CropVariety) => cv.ediCode === ediCode)?.name || '' : '');
  }

  private _filterCropVarieties(value: string | number | null): CropVariety[] {
    if (!value || value === '') {
      return this.data.cropVarieties; // Show all options when empty
    }

    if (typeof value === 'number') {
      return this.data.cropVarieties.filter((cv) => cv.ediCode === value);
    }

    const filterValue = value.toString().toLowerCase();
    return this.data.cropVarieties.filter((cv) => cv.name.toLowerCase().includes(filterValue) || cv.ediCode.toString().includes(filterValue));
  }

  ngOnInit(): void {
    // Initialize filtered crop varieties observable
    this.filteredCropVarieties$ = this.formGroup.get('cropVarietySelection')!.valueChanges.pipe(
      startWith(this.formGroup.get('cropVarietySelection')!.value || ''),
      map((value) => this._filterCropVarieties(value)),
    );

    // When a crop variety is selected, update the ediCode
    this.formGroup.get('cropVarietySelection')!.valueChanges.subscribe((selectedEdiCode) => {
      if (typeof selectedEdiCode === 'number') {
        this.formGroup.get('ediCode')!.setValue(selectedEdiCode);
      }
    });

    if (this.data.varietyProperty) {
      const { cropVarietyName, productionPurposeCategoryName, ...rest } = this.data.varietyProperty;
      this.formGroup.patchValue(rest);
      // Set the crop variety selection to the ediCode for display
      this.formGroup.get('cropVarietySelection')!.setValue(this.data.varietyProperty.ediCode);
    }
  }

  confirm(): void {
    if (this.formGroup.valid) {
      const { cropVarietySelection, ...formValue } = this.formGroup.value;
      this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm, result: formValue });
    }
  }

  cancel(): void {
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel });
  }
}
